import { CommonModule } from '@angular/common';
import { Component, ViewChild, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { BlockLoadingComponent } from '@merchant-portal/app/components/loading/block-loading/block-loading.component';
import { PinDialogMode, PinDialogStep } from '@merchant-portal/app/models/dialog/dialog-type';
import {
  PinDialogConfig,
  PinDialogResult,
} from '@merchant-portal/app/models/dialog/dialog.interface';
import { CodeInputComponent, CodeInputModule } from 'angular-code-input';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonComponent } from 'ui-component/src/public-api';
import { PinApiService } from '../api/pin-api.service';
import { PinDialogStore } from './store/pin-dialog.store';

@Component({
  selector: 'app-pin-dialog',
  templateUrl: './pin-dialog.component.html',
  styleUrls: ['./pin-dialog.component.scss'],
  providers: [PinApiService, PinDialogStore],
  imports: [
    CommonModule,
    CodeInputModule,
    CardModule,
    InputTextModule,
    ReactiveFormsModule,
    ButtonModule,
    BlockLoadingComponent,
    ButtonComponent,
  ],
})
export class PinDialogComponent {
  @ViewChild('codeInput') codeInput!: CodeInputComponent;

  readonly store = inject(PinDialogStore);
  private readonly ref = inject(DynamicDialogRef);
  private readonly config = inject(DynamicDialogConfig<PinDialogConfig>);

  private readonly mode = toSignal(this.store.mode$, {
    initialValue: PinDialogMode.ValidateExisting,
  });
  private readonly step = toSignal(this.store.currentStep$, {
    initialValue: PinDialogStep.EnterCurrent,
  });
  private readonly isPinValid = toSignal(this.store.isPinValid$, { initialValue: false });

  readonly errorMessage = toSignal(this.store.errorMessage$, { initialValue: '' });
  readonly isLoading = toSignal(this.store.isLoading$, { initialValue: false });

  private currentPinValue = signal('');
  isComplete = false;

  readonly title = computed(() => {
    const mode = this.mode();
    const step = this.step();

    if (step === PinDialogStep.Done && this.isPinValid() && mode === PinDialogMode.CreateNew) {
      return 'PIN Successfully Created';
    }

    const titleMap: Record<string, string> = {
      [PinDialogMode.ValidateExisting]: 'Enter Your PIN',
      [PinDialogMode.OnlyValidate]: 'Verify PIN',
      [PinDialogMode.CreateNew]: this.getTitleWhenCreatePinMode(this.step()),
      [PinDialogMode.ChangePin]: this.getTitleWhenChangePinMode(this.step()),
    };

    return this.config?.data?.title || titleMap[this.mode()] || 'PIN Required';
  });

  readonly subtitle = computed(() => {
    const mode = this.mode();
    const step = this.step();

    if (this.config?.data?.subtitle) {
      return this.config.data.subtitle;
    }

    if (step === PinDialogStep.Done && this.isPinValid() && mode === PinDialogMode.CreateNew) {
      return 'PIN is used as a two-step verification for the privacy and security of your Pivot account.';
    }

    const subtitleMap: Record<string, string> = {
      [PinDialogMode.ValidateExisting]: 'Please enter your PIN to continue',
      [PinDialogMode.OnlyValidate]: 'Enter your PIN for verification',
      [PinDialogMode.CreateNew]: this.getSubTitleWhenCreatePinMode(step),
      [PinDialogMode.ChangePin]: this.getSubTitleWhenChangePinMode(step),
    };

    return subtitleMap[mode] || '';
  });

  readonly actionButtonText = computed(() => {
    const mode = this.mode();
    const step = this.step();

    if (mode === PinDialogMode.ChangePin) {
      if (step === PinDialogStep.EnterCurrent) return 'Next';
      if (step === PinDialogStep.EnterNew) return 'Next';
      if (step === PinDialogStep.ConfirmNew) return 'Change PIN';
    }

    if (mode === PinDialogMode.CreateNew) {
      if (step === PinDialogStep.EnterNew) return 'Next';
      if (step === PinDialogStep.ConfirmNew) return 'Create PIN';
    }

    return 'Confirm';
  });

  readonly canProceed = computed(() => {
    return this.currentPinValue().length === 6;
  });

  constructor() {
    const dialogConfig = this.config?.data;

    if (dialogConfig) {
      this.store.setMode(dialogConfig.mode);
      this.store.setWithoutValidation(dialogConfig.shouldSkipValidation);
      this.store.setSkipDirectCreation(dialogConfig.shouldSkipDirectCreation);
    }

    effect(() => {
      const step = this.step();
      const isPinValid = this.isPinValid();

      if (step === PinDialogStep.Done && isPinValid && this.config?.data?.shouldSkipSuccessInfo) {
        this.handleSuccess();
        return;
      }

      if (step === PinDialogStep.Done && isPinValid) {
        this.isComplete = true;
      }
    });
  }

  onCodeChanged(value: string): void {
    this.currentPinValue.set(value);
    const step = this.step();

    switch (step) {
      case PinDialogStep.EnterCurrent:
        this.store.setCurrentPin(value);
        break;
      case PinDialogStep.EnterNew:
        this.store.setNewPin(value);
        break;
      case PinDialogStep.ConfirmNew:
        this.store.setConfirmPin(value);
        break;
    }

    if (step === PinDialogStep.ConfirmNew && value.length == 6) {
      this.store.validateNewPin();

      if (this.errorMessage() == 'PIN doesn’t match the new PIN') {
        this.clearInput();
      }
    }
  }

  proceed(): void {
    if (!this.canProceed()) return;

    const mode = this.mode();
    const step = this.step();

    switch (step) {
      case PinDialogStep.EnterCurrent:
        if (mode === PinDialogMode.ValidateExisting || mode === PinDialogMode.OnlyValidate) {
          this.store.validateCurrentPin();
        } else if (mode === PinDialogMode.ChangePin) {
          this.store.validateCurrentPin();
        }
        break;

      case PinDialogStep.EnterNew:
        if (mode === PinDialogMode.CreateNew) {
          this.store.validateNewPin();
        } else if (mode === PinDialogMode.ChangePin) {
          this.store.validateNewPin();
        }
        break;

      case PinDialogStep.ConfirmNew:
        if (mode === PinDialogMode.CreateNew) {
          this.store.confirmNewPin();
          this.store.createPin();
        } else if (mode === PinDialogMode.ChangePin) {
          this.store.confirmNewPin();
          this.store.changePin();
        }
        break;
    }

    this.clearInput();
  }

  cancel(): void {
    const result: PinDialogResult = {
      isSuccess: false,
    };
    this.ref.close(result);
  }

  formInputShouldError(): boolean {
    return (this.errorMessage()?.length ?? 0) > 0 && this.currentPinValue() == '';
  }

  handleSuccess(): void {
    this.store.state$.subscribe(state => {
      const result: PinDialogResult = {
        isSuccess: true,
        currentPin: state.currentPin,
        newPin: state.newPin,
      };
      this.ref.close(result);
    });
  }

  private clearInput(): void {
    if (this.codeInput) {
      this.codeInput.reset();
    }
    this.currentPinValue.set('');
  }

  private getTitleWhenChangePinMode(step: string): string {
    if (step === PinDialogStep.EnterCurrent) {
      return 'Enter Current PIN';
    }

    if (step === PinDialogStep.EnterNew) {
      return 'Enter New PIN';
    }

    return 'Confirm New PIN';
  }

  private getSubTitleWhenChangePinMode(step: string): string {
    if (step === PinDialogStep.EnterCurrent) {
      return 'Please enter your current PIN';
    }

    if (step === PinDialogStep.EnterNew) {
      return 'Please enter your new PIN';
    }

    return 'Please confirm your new PIN';
  }

  private getTitleWhenCreatePinMode(step: string): string {
    if (step === PinDialogStep.ConfirmNew) {
      return 'Confirm PIN';
    }

    return 'Create PIN';
  }

  private getSubTitleWhenCreatePinMode(step: string): string {
    if (step === PinDialogStep.ConfirmNew) {
      return 'Re-enter your 6-digit PIN';
    }

    return 'Enter a 6-digit number PIN';
  }
}
